import { getFirebasePublicConfigStatus } from "./firebaseClient";

const REELITPRO_ANDROID_APP_REFERRER =
  "android-app://com.reelitpro.app";
const PERMISSION_STATES = new Set(["default", "granted", "denied"]);

function hasTrustedTwaReferrer(documentObject) {
  const referrer = String(documentObject?.referrer || "");
  return (
    referrer === REELITPRO_ANDROID_APP_REFERRER ||
    referrer.startsWith(`${REELITPRO_ANDROID_APP_REFERRER}/`)
  );
}

function matchesDisplayMode(windowObject, mode) {
  try {
    return windowObject?.matchMedia?.(`(display-mode: ${mode})`)?.matches === true;
  } catch {
    return false;
  }
}

function isStandalone(windowObject, navigatorObject) {
  return (
    ["standalone", "fullscreen", "minimal-ui"].some((mode) =>
      matchesDisplayMode(windowObject, mode)
    ) || navigatorObject?.standalone === true
  );
}

function isIosLike(navigatorObject) {
  const userAgent = String(navigatorObject?.userAgent || "");
  const platform = String(navigatorObject?.platform || "");
  return (
    /iPad|iPhone|iPod/i.test(userAgent) ||
    (platform === "MacIntel" && Number(navigatorObject?.maxTouchPoints) > 1)
  );
}

function getContext(windowObject, navigatorObject, documentObject) {
  if (hasTrustedTwaReferrer(documentObject)) {
    return { platform: "twa", appContext: "twa" };
  }
  if (isStandalone(windowObject, navigatorObject)) {
    return { platform: "web", appContext: "standalone" };
  }
  return { platform: "web", appContext: "browser" };
}

export function getPushCapability(options = {}) {
  const windowObject =
    options.windowObject === undefined
      ? typeof window !== "undefined"
        ? window
        : null
      : options.windowObject;
  const navigatorObject =
    options.navigatorObject === undefined
      ? typeof navigator !== "undefined"
        ? navigator
        : null
      : options.navigatorObject;
  const documentObject =
    options.documentObject === undefined
      ? typeof document !== "undefined"
        ? document
        : null
      : options.documentObject;
  const environment = options.environment ?? process.env;
  const configStatus = getFirebasePublicConfigStatus(environment);
  const context = getContext(windowObject, navigatorObject, documentObject);
  const notificationApi = windowObject?.Notification;

  let permissionState = "unsupported";
  try {
    const permission = notificationApi?.permission;
    if (PERMISSION_STATES.has(permission)) permissionState = permission;
  } catch {
    permissionState = "unsupported";
  }

  const secureContext = windowObject?.isSecureContext === true;
  const browserAvailable = Boolean(windowObject && navigatorObject);
  const notificationAvailable = Boolean(notificationApi);
  const serviceWorkerAvailable = Boolean(navigatorObject?.serviceWorker);
  const pushManagerAvailable = Boolean(windowObject?.PushManager);
  const supported = Boolean(
    browserAvailable &&
      secureContext &&
      notificationAvailable &&
      serviceWorkerAvailable &&
      pushManagerAvailable &&
      !isIosLike(navigatorObject)
  );

  let code = null;
  if (!browserAvailable) code = "firebase_client_unsupported";
  else if (!configStatus.configured) code = "firebase_client_unconfigured";
  else if (!supported) code = "push_api_unavailable";

  return {
    supported,
    configured: configStatus.configured,
    secureContext,
    permissionState,
    ...context,
    code,
  };
}
